import { useState } from 'react'
import type { ActionItem } from '../external-types'
import testOpsService from '../api'

// Human-friendly relative timestamp — "just now", "3m ago", "2h ago", "5d ago",
// falling back to a date for anything older than ~4 weeks. Used in the My
// Actions drawer where absolute timestamps are noise.
function formatRelativeTime(input: string | Date | undefined | null) {
  if (!input) return ''
  const then = input instanceof Date ? input.getTime() : new Date(input).getTime()
  if (Number.isNaN(then)) return ''
  const diffSec = Math.max(0, (Date.now() - then) / 1000)
  if (diffSec < 45) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`
  if (diffSec < 86400 * 28) return `${Math.floor(diffSec / (86400 * 7))}w ago`
  return new Date(then).toLocaleDateString()
}

export function useMyActions(sendMessage: (msg: string) => void) {
  const [actionItems, setActionItems] = useState<ActionItem[]>([])
  const [loadingActions, setLoadingActions] = useState(false)
  const [actionFilter, setActionFilter] = useState<'all' | 'jira' | 'github' | 'critical'>('all')
  const [showCompletedActions, setShowCompletedActions] = useState(false)
  const [showMyActions, setShowMyActions] = useState(false)

  const loadActionItems = async () => {
    setLoadingActions(true)
    try {
      const items = await testOpsService.getActionItems()
      setActionItems(items)
    } catch (error) {
      console.error('Failed to load action items:', error)
    } finally {
      setLoadingActions(false)
    }
  }

  // Hand a My Actions item off to the chat AI as a structured prompt so the
  // user can have a contextual conversation about it (recommend next steps,
  // dig into related issues, retest, file a bug, etc.) instead of just
  // marking it complete. The chat already has Jira/GitHub MCP tools, so it
  // can fetch additional details on its own if needed.
  const handleActionExplore = (item: ActionItem) => {
    const cu = item.metadata?.connectionUser
    const lines: string[] = []

    lines.push(
      "I'd like your help with this action item. Please understand the full " +
      "context, propose what I should do next, and we can keep discussing in " +
      "this thread."
    )
    lines.push('')
    lines.push(`**${item.title}**`)
    if (item.description) {
      lines.push(item.description)
    }
    lines.push('')

    const facts: string[] = []
    if (item.metadata?.source) facts.push(`Source: ${item.metadata.source}`)
    if (item.type) facts.push(`Type: ${String(item.type).replace(/_/g, ' ')}`)
    if (item.priority) facts.push(`Priority: ${item.priority}`)
    if (item.status) facts.push(`Status: ${String(item.status).replace(/_/g, ' ')}`)
    if (item.metadata?.connection) facts.push(`Connection: ${item.metadata.connection}`)
    if (cu?.email || cu?.displayName) {
      facts.push(`Assigned to: ${cu.email || cu.displayName}`)
    }
    if (item.tags && item.tags.length > 0) {
      facts.push(`Labels: ${item.tags.join(', ')}`)
    }
    if (item.metadata?.url) facts.push(`Link: ${item.metadata.url}`)
    if (facts.length > 0) {
      facts.forEach(f => lines.push(`- ${f}`))
      lines.push('')
    }

    lines.push(
      "What do you recommend I do? If you need more detail (full description, " +
      "comments, related PRs, recent activity, etc.), fetch it and walk me " +
      "through your reasoning before suggesting actions."
    )

    const prompt = lines.join('\n')

    // Close the side panel so the chat thread is visible, then seed it.
    setShowMyActions(false)
    void sendMessage(prompt)
  }

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  const pendingItemsAll = actionItems.filter((i) => i.status === 'pending')
  const inProgressItems = actionItems.filter((i) => i.status === 'in_progress')
  const completedItems = actionItems.filter((i) => i.status === 'completed')

  const jiraPendingCount = pendingItemsAll.filter(
    (i) => i.metadata?.source === 'jira'
  ).length
  const githubPendingCount = pendingItemsAll.filter(
    (i) => i.metadata?.source === 'github'
  ).length
  const criticalPendingCount = pendingItemsAll.filter(
    (i) => i.priority === 'critical'
  ).length

  const matchesActionFilter = (item: ActionItem): boolean => {
    if (actionFilter === 'all') return true
    if (actionFilter === 'jira') return item.metadata?.source === 'jira'
    if (actionFilter === 'github') return item.metadata?.source === 'github'
    if (actionFilter === 'critical') return item.priority === 'critical'
    return true
  }

  const filteredPending = pendingItemsAll.filter(matchesActionFilter)

  // Group filtered pending items by source so the drawer renders "Jira"
  // and "GitHub" sections instead of one flat list. Items without a known
  // source bucket into "other".
  const pendingBySource = filteredPending.reduce<Record<string, ActionItem[]>>(
    (acc, item) => {
      const key = String(item.metadata?.source || 'other')
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    },
    {}
  )

  const priorityStripeClass = (priority: string | undefined) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500'
      case 'high':
        return 'bg-orange-500'
      case 'medium':
        return 'bg-yellow-400'
      case 'low':
        return 'bg-blue-300'
      default:
        // Theme-adaptive neutral for "no priority" — gray-300 looked the same
        // in both modes and disappeared on dark surfaces.
        return 'bg-foreground/30'
    }
  }

  const actionFilters: Array<{
    key: typeof actionFilter
    label: string
    count: number
  }> = [
    { key: 'all', label: 'All', count: pendingItemsAll.length },
    { key: 'jira', label: 'Jira', count: jiraPendingCount },
    { key: 'github', label: 'GitHub', count: githubPendingCount },
    { key: 'critical', label: 'Critical', count: criticalPendingCount },
  ]

  const totalVisibleWork = filteredPending.length + inProgressItems.length

  return {
    actionItems,
    loadingActions,
    actionFilter,
    setActionFilter,
    showCompletedActions,
    setShowCompletedActions,
    showMyActions,
    setShowMyActions,
    loadActionItems,
    handleActionExplore,
    pendingItemsAll,
    inProgressItems,
    completedItems,
    filteredPending,
    pendingBySource,
    priorityStripeClass,
    actionFilters,
    totalVisibleWork,
    formatRelativeTime,
  }
}
